import { useCallback, useEffect, useState } from "react"
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom"
import { useSession } from "./auth/session"
import LoginPage from "./pages/LoginPage"
import RestaurantsPage from "./pages/RestaurantsPage"
import MenuPage from "./pages/MenuPage"
import SearchPage from "./pages/SearchPage"
import AccessDenied from "./pages/AccessDenied"
import IdleLock from "./components/IdleLock"
import InstallGate from "./components/InstallGate"

export default function App() {
  // Hands over from the boot screen in index.html once the first screen has committed.
  useEffect(() => {
    document.getElementById("boot")?.remove()
  }, [])

  return (
    <InstallGate>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/restaurants" element={<RestaurantsRoute />} />
        <Route path="/menu" element={<MenuRoute />} />
        <Route path="/search" element={<SearchRoute />} />
        <Route path="*" element={<Navigate to="/menu" replace />} />
      </Routes>
      <IdleLock />
    </InstallGate>
  )
}

function LoginRoute() {
  const { isAuthenticated } = useSession()
  const location = useLocation()
  if (isAuthenticated) {
    return <Navigate to={location.state?.from ?? "/menu"} replace />
  }
  return <LoginPage />
}

// Marks a screen pushed on top of the menu, so leaving it pops back instead of stacking another menu.
const PUSHED_FROM_MENU = { fromMenu: true }

function useReturnToMenu() {
  const navigate = useNavigate()
  const location = useLocation()
  const isPushedFromMenu = location.state?.fromMenu === true

  return useCallback(() => {
    if (isPushedFromMenu) navigate(-1)
    else navigate("/menu", { replace: true })
  }, [isPushedFromMenu, navigate])
}

function RestaurantsRoute() {
  const { isAuthenticated, user, restaurant } = useSession()
  const returnToMenu = useReturnToMenu()
  const [canGoBack] = useState(() => Boolean(restaurant))

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!user?.isRestaurantAdmin) return <AccessDenied />

  return <RestaurantsPage onDone={returnToMenu} onCancel={canGoBack ? returnToMenu : null} />
}

function MenuRoute() {
  const { isAuthenticated, user, restaurant } = useSession()
  const navigate = useNavigate()

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!user?.isRestaurantAdmin) return <AccessDenied />
  if (!restaurant) return <Navigate to="/restaurants" replace />

  return (
    <MenuPage
      onSwitchRestaurant={() => navigate("/restaurants", { state: PUSHED_FROM_MENU })}
      onOpenSearch={() => navigate("/search", { state: PUSHED_FROM_MENU })}
    />
  )
}

function SearchRoute() {
  const { isAuthenticated, user, restaurant } = useSession()
  const returnToMenu = useReturnToMenu()

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!user?.isRestaurantAdmin) return <AccessDenied />
  if (!restaurant) return <Navigate to="/restaurants" replace />

  return <SearchPage onClose={returnToMenu} />
}
